import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { GlassCard } from "../../components/ui/glass-card";
import { PoweredByFooter } from "../../components/PoweredByFooter";
import { axiosInstance, apiClient } from "../../services/apiClient";
import { AcceptInviteRequest } from "../../generated/api-client";
import toast from "react-hot-toast";
import { PasswordRequirementsChecklist } from "../../components/PasswordRequirementsChecklist";
import { describeMissingRequirements, validatePassword } from "../../utils/passwordPolicy";

const InvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [token, setToken] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<{ email: string; role: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (!tokenParam) {
      setError("Token di invito non fornito");
      setLoading(false);
      return;
    }
    setToken(tokenParam);
    fetchInviteInfo(tokenParam);
  }, [searchParams]);

  const fetchInviteInfo = async (inviteToken: string) => {
    try {
      const response = await axiosInstance.get(`/auth/invite?token=${encodeURIComponent(inviteToken)}`);
      if (response.data.valid) {
        setInviteInfo({ email: response.data.email, role: response.data.role });
      } else {
        setError(response.data.error || "Token non valido o scaduto");
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Errore nel caricamento dell'invito");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return toast.error("Token mancante");
    if (!firstname.trim()) return toast.error("Inserisci il nome");
    if (!lastname.trim()) return toast.error("Inserisci il cognome");
    if (!password.trim()) return toast.error("Inserisci la password");
    if (!passwordConfirm.trim()) return toast.error("Conferma la password");
    if (password !== passwordConfirm) return toast.error("Le password non coincidono");
    const missingRequirements = describeMissingRequirements(password);
    if (missingRequirements) return toast.error(missingRequirements);

    setSubmitting(true);
    try {
      const request = new AcceptInviteRequest({
        token,
        password,
        password_confirm: passwordConfirm,
        firstname: firstname.trim(),
        lastname: lastname.trim(),
      });
      await apiClient.accept_invite_auth_invite_post(request);
      toast.success("Registrazione completata! Ora puoi accedere.");
      navigate("/login");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Errore nell'accettazione dell'invito");
    } finally {
      setSubmitting(false);
    }
  };

  const primaryColor = theme.palette.primary.main;
  const primaryLightColor = theme.palette.primary.light;

  const getRoleLabel = (role: string) =>
    ({ baseUser: "Utente Base", mantainer: "Mantainer", admin: "Amministratore" } as Record<string, string>)[role] || role;

  return (
    <Box sx={{ minHeight: "100vh", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", p: 2, backgroundColor: theme.palette.background.default }}>
      <GlassCard hover={false} className="w-full p-4 md:p-8 relative z-10 w-[28rem]">
        <Button
          variant="text"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/login")}
          sx={{ position: "absolute", top: 16, left: 16, color: theme.palette.text.primary, minWidth: "auto" }}
        />

        <div className="flex justify-center mb-6">
          <img src="/assets/long_light.png" alt="logo" className="h-12 rounded-lg" />
        </div>

        <Typography
          variant="h5"
          component="h1"
          align="center"
          sx={{
            mb: 4,
            fontWeight: 600,
            background: `linear-gradient(135deg, ${primaryColor}, ${primaryLightColor})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Accetta Invito
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 2 }}>
            <CircularProgress sx={{ mx: "auto", color: primaryColor }} />
            <Typography sx={{ color: theme.palette.text.secondary }}>Caricamento informazioni invito...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="error">{error}</Alert>
            <Button onClick={() => navigate("/login")} variant="outlined" fullWidth>
              Torna al Login
            </Button>
          </Box>
        ) : inviteInfo ? (
          <Box component="form" onSubmit={handleAcceptInvite} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Alert severity="info">Sei stato invitato come <strong>{getRoleLabel(inviteInfo.role)}</strong></Alert>
            <TextField label="Email" type="email" value={inviteInfo.email} fullWidth variant="outlined" />
            <TextField label="Nome" value={firstname} onChange={(e) => setFirstname(e.target.value)} fullWidth required />
            <TextField label="Cognome" value={lastname} onChange={(e) => setLastname(e.target.value)} fullWidth required />
            <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth required />
            {password.length > 0 && <PasswordRequirementsChecklist password={password} />}
            <TextField label="Conferma Password" type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} fullWidth required />
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !validatePassword(password).valid || password !== passwordConfirm}
              fullWidth
              sx={{
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                background: `linear-gradient(135deg, ${primaryLightColor}, ${primaryColor})`,
              }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : "Conferma Registrazione"}
            </Button>
          </Box>
        ) : null}
      </GlassCard>

      <PoweredByFooter absolute />
    </Box>
  );
};

export default InvitePage;

